import type { EmailSettings } from '../../types';
import { Tooltip, Icon, Checkbox } from '@workramp/ui';

import isFeatureEnabled from '~/components/common/is-feature-enabled';
import { SettingsHeader, HeadingWrapper } from './NotificationSettings.styles';

const GDPR_CONSENT_FF = isFeatureEnabled('gdpr_email_compliance');

export interface NotificationSettingProps {
  onSettingChange: (notificationName: string, isChecked: boolean) => void;
  includeDigests?: boolean;
  feedbackDisabled?: boolean;
  hasEnterpriseReminders?: boolean;
  dailyDigestDisabled?: boolean;
  weeklyDigestDisabled?: boolean;
  emailSettings: EmailSettings;
}

export function NotificationSettings({
  onSettingChange,
  includeDigests,
  hasEnterpriseReminders,
  feedbackDisabled,
  dailyDigestDisabled,
  weeklyDigestDisabled,
  emailSettings,
}: NotificationSettingProps) {
  // Begin Translations
  const { I18n } = window;
  const TRANSLATED_NOTIFICATION_SETTINGS = I18n.t('js.learner.profile.notification_settings');
  const TRANSLATED_NOTIFICATION_INFO = I18n.t('js.learner.profile.notification_info');
  const TRANSLATED_CONTENT_TYPE_DISPLAY_NAME_PLURAL = I18n.t('common.academy.content.trainings');

  const TRANSLATED_MARKETING_CONSENT = I18n.t('js.learner.profile.notifications.marketing_consent');
  const TRANSLATED_DAILY_DIGEST = I18n.t('js.learner.profile.notifications.daily_digest');
  const TRANSLATED_WEEKLY_DIGEST = I18n.t('js.learner.profile.notifications.weekly_digest');
  const TRANSLATED_GUIDE_ASSIGNED = I18n.t('js.learner.profile.notifications.guide_assigned', {
    content: TRANSLATED_CONTENT_TYPE_DISPLAY_NAME_PLURAL,
  });
  const TRANSLATED_GUIDE_PROGRESS = I18n.t('js.learner.profile.notifications.guide_progress', {
    content: TRANSLATED_CONTENT_TYPE_DISPLAY_NAME_PLURAL,
  });
  const TRANSLATED_FEEDBACK = I18n.t('js.learner.profile.notifications.feedback');
  const TRANSLATED_DISABLED_MESSAGE = I18n.t('js.learner.profile.notifications.disabled_message');
  const TRANSLATED_REMINDERS_CONFIGURED_MESSAGE = I18n.t('js.learner.profile.notifications.reminders_configured');
  const TRANSLATED_NO_REMINDERS_MESSAGE = I18n.t('js.learner.profile.notifications.no_reminders');

  // End Translations

  return (
    <>
      <SettingsHeader>
        <HeadingWrapper weight="semibold" size={2}>
          {TRANSLATED_NOTIFICATION_SETTINGS}
        </HeadingWrapper>
        <Tooltip message={TRANSLATED_NOTIFICATION_INFO}>
          <Icon name="info:regular" />
        </Tooltip>
      </SettingsHeader>
      <Checkbox
        isSelected={emailSettings?.on_guide_assignment}
        onChange={(checked) => onSettingChange('on_guide_assignment', checked)}
      >
        {TRANSLATED_GUIDE_ASSIGNED}
      </Checkbox>
      <Checkbox
        isSelected={emailSettings?.guide_assignment_reminders}
        onChange={(checked) => onSettingChange('guide_assignment_reminders', checked)}
        description={hasEnterpriseReminders ? TRANSLATED_REMINDERS_CONFIGURED_MESSAGE : TRANSLATED_NO_REMINDERS_MESSAGE}
      >
        {TRANSLATED_GUIDE_PROGRESS}
      </Checkbox>
      <Checkbox
        isSelected={feedbackDisabled || emailSettings?.on_feedback_message}
        onChange={(checked) => onSettingChange('on_feedback_message', checked)}
        isDisabled={feedbackDisabled}
        description={feedbackDisabled ? TRANSLATED_DISABLED_MESSAGE : undefined}
      >
        {TRANSLATED_FEEDBACK}
      </Checkbox>
      {includeDigests && (
        <Checkbox
          isDisabled={dailyDigestDisabled}
          description={dailyDigestDisabled ? TRANSLATED_DISABLED_MESSAGE : undefined}
          isSelected={dailyDigestDisabled || emailSettings?.daily_digest}
          onChange={(checked) => onSettingChange('daily_digest', checked)}
        >
          {TRANSLATED_DAILY_DIGEST}
        </Checkbox>
      )}
      {includeDigests && (
        <Checkbox
          isDisabled={weeklyDigestDisabled}
          description={weeklyDigestDisabled ? TRANSLATED_DISABLED_MESSAGE : undefined}
          isSelected={weeklyDigestDisabled || emailSettings?.weekly_digest}
          onChange={(checked) => onSettingChange('weekly_digest', checked)}
        >
          {TRANSLATED_WEEKLY_DIGEST}
        </Checkbox>
      )}
      {GDPR_CONSENT_FF && (
        <Checkbox
          isSelected={emailSettings?.marketing_consent}
          onChange={(checked) => onSettingChange('marketing_consent', checked)}
        >
          {TRANSLATED_MARKETING_CONSENT}
        </Checkbox>
      )}
    </>
  );
}
